<template>
  <div class="p-6 bg-gray-100">

    <!-- Header + Upload Button -->
    <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-4">
      <h3 class="text-xl font-semibold text-text-primary">Existing Files</h3>
      <button @click="openUploadModal(null)"
        class="flex items-center justify-center gap-3 bg-primary-dark text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:bg-orange-400 transition-all duration-200 hover:shadow-xl">
        <span class="text-xl">📄</span>
        <span>Upload Document(s)</span>
      </button>
    </div>

    <DocumentFileListsCard :fileList="fileList" :selectedIds="selectedIds" :currentPage="currentPage"
      :totalPages="totalPages" :totalItems="totalItems" :pageSize="pageSize" @toggle-select="toggleSelect"
      @toggle-status="toggleDocumentStatus" @edit="openUploadModal" @download="downloadFile"
      @change-page-size="changePageSize" @next-page="nextPage" @prev-page="prevPage" />

    <!-- Upload Modal -->
    <DocumentFileUploadModal :show="showModal" :section-id="sectionId" :file-to-edit="editingFile" @close="closeModal"
      @uploaded="refreshFiles" />

  </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { updateDocument, downloadDocument } from "@/api/documentFiles";
import { getDocumentGroupsBySectionId } from "@/api/documentGroup";
import DocumentFileUploadModal from "@/components/modals/DocumentFileUploadModal.vue";
import DocumentFileListsCard from "@/components/ui/DocumentFileListsCard.vue";

export default {
  components: { DocumentFileUploadModal, DocumentFileListsCard },

  setup() {
    const { proxy } = getCurrentInstance();

    const showModal = ref(false);
    const editingFile = ref(null);
    const fileList = ref([]);
    const selectedIds = ref([]);

    // Pagination Vars
    const currentPage = ref(1);
    const pageSize = ref(10);
    const totalPages = ref(1);
    const totalItems = ref(0);

    const sectionId = computed(() => {
      const id = Number(proxy.$route.query.sectionId);
      return isNaN(id) ? null : id;
    });

    const fetchFiles = async () => {
      if (!sectionId.value) return;

      try {
        const res = await getDocumentGroupsBySectionId(sectionId.value, {
          page: currentPage.value,
          limit: pageSize.value
        })

        const group = res.data?.data;
        const pagination = res.data?.pagination;

        if (!group) {
          fileList.value = [];
          return;
        }

        fileList.value =
          group.documents?.map((doc) => ({
            id: doc.id,
            filename: doc.filename,
            original_filename: doc.original_filename,
            description: doc.description,
            group_id: group.id,
            file_size_bytes: doc.file_size,
            file_size: formatFileSize(doc.file_size),
            file_type: doc.file_type,
            status: doc.status,
            timestamp: doc.createdAt,
          })) || [];

        if (pagination) {
          currentPage.value = pagination.page;
          totalPages.value = pagination.totalPages;
          totalItems.value = pagination.total;
        }
      } catch (err) {
        console.error(err);
      }
    };

    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
        fetchFiles();
      }
    };

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--;
        fetchFiles();
      }
    };

    const changePageSize = () => {
      currentPage.value = 1;
      fetchFiles();
    };

    const openUploadModal = (file = null) => {
      editingFile.value = file;
      showModal.value = true;
    };

    const closeModal = () => {
      showModal.value = false;
      editingFile.value = null;
    };

    const refreshFiles = async () => {
      await fetchFiles();
      selectedIds.value = [];
      closeModal();
    };

    const toggleSelect = (file) => {
      const idx = selectedIds.value.indexOf(file.id);
      if (idx === -1) selectedIds.value.push(file.id);
      else selectedIds.value.splice(idx, 1);
    };

    const toggleDocumentStatus = async (file) => {
      const newStatus = file.status === "active" ? "inactive" : "active";
      await updateDocument(file.id, { status: newStatus });
      file.status = newStatus;
    };

    const downloadFile = async (file) => {
      await downloadDocument(file.id, file.original_filename);
    };

    const formatFileSize = (bytes) => {
      if (!bytes && bytes !== 0) return "";
      const kb = bytes / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      return `${(kb / 1024).toFixed(2)} MB`;
    };

    onMounted(fetchFiles);

    return {
      fileList,
      showModal,
      sectionId,
      editingFile,
      selectedIds,

      pageSize,
      totalPages,
      totalItems,
      currentPage,

      closeModal,
      refreshFiles,
      toggleSelect,
      downloadFile,
      openUploadModal,
      toggleDocumentStatus,

      nextPage,
      prevPage,
      changePageSize,
    };
  },
};
</script>